import { useMemo, useRef, useState, type DragEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { Download, Eye, EyeOff, File as FileIcon, Search, Trash2, Upload, X } from 'lucide-react';
import { Button, ConfirmDialog, EmptyState, ErrorCard, SkeletonTable, cn } from '@/components/ui';
import { DataTable, PageHeader, Section } from '@/components/layout/PageLayout';
import {
  downloadInstaller,
  downloadTenantInstaller,
  useDeleteInstaller,
  useDeleteTenantInstaller,
  useInstallers,
  useInvalidateTenantInstallers,
  useTenantInstallers,
  useUploadInstaller,
  useUploadTenantInstaller,
  type TenantConsoleTarget,
} from '@/api/agentUpdate';
import { useTenants } from '@/api/tenants';
import { getErrorMessage } from '@/lib/errors';
import { showToast } from '@/lib/toast';
import type { AgentInstaller, AgentOs, TenantInstaller } from '@/types/agentUpdate';

const OS_OPTIONS: { value: AgentOs; label: string; badge: string }[] = [
  { value: 'win', label: 'Windows', badge: 'bg-state-info/15 text-state-info' },
  { value: 'lin', label: 'Linux', badge: 'bg-state-warning/15 text-state-warning' },
  { value: 'mac', label: 'macOS', badge: 'bg-state-success/15 text-state-success' },
];
const osMeta = (os: AgentOs) => OS_OPTIONS.find((o) => o.value === os)!;

// Stricter than the backend's VERSION_PATTERN on purpose: plain numeric
// major.minor.patch only (1.0.1), so "v1.0.1" or "1.0.1-rc1" are rejected.
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;
const ACCEPT_HINT = '.zip archive — max 1 GB';

const inputCls =
  'w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-xs text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20';

const formatSize = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${(b / 1e6).toFixed(1)} MB`);

type Tab = 'installers' | 'tenant';

// ── Shared upload form pieces ────────────────────────────────────────────

function FileDrop({ file, onFile, disabled }: { file: File | null; onFile: (f: File) => void; disabled: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f && !disabled) onFile(f);
  };

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          'cursor-pointer rounded-lg border border-dashed border-border-default bg-surface-1 px-3 py-6 text-center text-xs text-text-secondary transition-colors hover:border-accent hover:bg-accent/5',
          over && 'border-accent bg-accent/5',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <Upload className="mx-auto mb-1.5 h-5 w-5 text-text-tertiary" />
        <div>
          Drag &amp; drop installer here, or <span className="font-medium text-accent">browse</span>
        </div>
        <div className="mt-0.5 text-[11px] text-text-tertiary">{ACCEPT_HINT}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {file && (
        <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-1 px-2.5 py-2 text-xs">
          <FileIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
          <span className="truncate text-text-primary">{file.name}</span>
          <span className="ml-auto shrink-0 text-text-tertiary">{formatSize(file.size)}</span>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="h-1 overflow-hidden rounded-full bg-surface-2">
      <div className="h-full bg-accent transition-[width]" style={{ width: `${percent}%` }} />
    </div>
  );
}

function validate(os: string, version: string, file: File | null, apiKey?: string): string {
  if (apiKey !== undefined && !apiKey.trim()) return "Enter the tenant console's API key.";
  if (!os) return 'Select an operating system.';
  if (!VERSION_PATTERN.test(version.trim())) return 'Enter a valid version, e.g. 1.0.3.';
  if (!file) return 'Choose an installer file.';
  if (!file.name.toLowerCase().endsWith('.zip')) return 'Installer must be a .zip archive.';
  return '';
}

/** OS + version + file fields with submit — shared by the general upload dialog and the tenant tab. */
function UploadFields({
  tenantId,
  apiKey,
  onUploaded,
  footerLeading,
}: {
  /** undefined = general installer; '' = tenant upload with no tenant picked yet (submit disabled). */
  tenantId?: string;
  /** Tenant console API key; required (and only used) for tenant uploads. */
  apiKey?: string;
  onUploaded: () => void;
  footerLeading?: ReactNode;
}) {
  const [os, setOs] = useState<AgentOs | ''>('');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [localError, setLocalError] = useState('');
  const general = useUploadInstaller();
  const tenant = useUploadTenantInstaller();
  const { isPending, error, reset: resetMutation } = tenantId === undefined ? general : tenant;

  const reset = () => {
    setOs('');
    setVersion('');
    setFile(null);
    setProgress(0);
    setLocalError('');
    resetMutation();
  };

  const submit = () => {
    const err = validate(os, version, file, tenantId === undefined ? undefined : apiKey);
    setLocalError(err);
    if (err || !os || !file) return;
    setProgress(0);
    const input = { os, version: version.trim(), file, onProgress: setProgress };
    const options = {
      onSuccess: () => {
        showToast(tenantId ? `Installer uploaded for ${tenantId}` : 'Installer uploaded');
        reset();
        onUploaded();
      },
    };
    if (tenantId === undefined) general.mutate(input, options);
    else tenant.mutate({ ...input, tenantId, apiKey: apiKey ?? '' }, options);
  };

  const errorText = localError || (error ? getErrorMessage(error) : '');

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Operating system</label>
          <select
            className={inputCls}
            value={os}
            onChange={(e) => setOs(e.target.value as AgentOs | '')}
            disabled={isPending}
          >
            <option value="">Select OS…</option>
            {OS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Version</label>
          <input
            className={cn(inputCls, 'font-mono')}
            placeholder="e.g. 1.0.3"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={isPending}
          />
        </div>
      </div>

      <FileDrop file={file} onFile={setFile} disabled={isPending} />
      {isPending && <ProgressBar percent={progress} />}
      {errorText && <p className="text-xs text-state-danger">{errorText}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        {footerLeading}
        <Button type="button" variant="secondary" size="sm" onClick={reset} disabled={isPending}>
          Reset
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          loading={isPending}
          leadingIcon={<Upload className="h-3.5 w-3.5" />}
          onClick={submit}
          disabled={tenantId === ''}
        >
          Upload
        </Button>
      </div>
    </>
  );
}

// ── Installers tab ───────────────────────────────────────────────────────

function UploadDialog({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-border-default bg-surface-0 p-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Upload installer"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">Upload installer</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <UploadFields
          onUploaded={onClose}
          footerLeading={
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
          }
        />
      </div>
    </div>
  );
}

function InstallersTab() {
  const [search, setSearch] = useState('');
  const [osFilter, setOsFilter] = useState<AgentOs | ''>('');
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<AgentInstaller | null>(null);
  const { data, isLoading, isError, error, refetch } = useInstallers();
  const { mutate: deleteInstaller, isPending: isDeleting } = useDeleteInstaller();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter(
      (i) =>
        (!osFilter || i.os === osFilter) &&
        (!q || i.fileName.toLowerCase().includes(q) || i.version.toLowerCase().includes(q)),
    );
  }, [data, search, osFilter]);

  const handleDownload = async (i: AgentInstaller) => {
    setDownloadingId(i.id);
    try {
      await downloadInstaller(i);
    } catch (err) {
      showToast(getErrorMessage(err, 'Download failed'), 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleting) return;
    deleteInstaller(deleting.id, {
      onSuccess: () => {
        showToast('Installer deleted');
        setDeleting(null);
      },
      onError: () => setDeleting(null),
    });
  };

  if (isError) {
    return (
      <ErrorCard title="Failed to load installers" message={getErrorMessage(error)} onRetry={() => void refetch()} />
    );
  }

  return (
    <Section>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-56">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search installers…"
            className={cn(inputCls, 'py-1.5 pl-8')}
          />
        </div>
        <select
          className={cn(inputCls, 'w-auto py-1.5')}
          value={osFilter}
          onChange={(e) => setOsFilter(e.target.value as AgentOs | '')}
        >
          <option value="">All OS</option>
          {OS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="sm"
          className="ml-auto"
          leadingIcon={<Upload className="h-3.5 w-3.5" />}
          onClick={() => setUploading(true)}
        >
          Upload installer
        </Button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={4} />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={<Upload />}
          title="No installers yet"
          description="Upload your first installer to get started"
          size="sm"
        />
      ) : (
        <DataTable<AgentInstaller>
          rows={rows}
          empty="No installers match your filters."
          columns={[
            {
              key: 'file',
              label: 'File',
              render: (i) => (
                <div className="flex items-center gap-2">
                  <FileIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                  <span className="truncate font-mono text-text-primary">{i.fileName}</span>
                </div>
              ),
            },
            {
              key: 'os',
              label: 'OS',
              width: '110px',
              render: (i) => (
                <span
                  className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold', osMeta(i.os).badge)}
                >
                  {osMeta(i.os).label}
                </span>
              ),
            },
            {
              key: 'version',
              label: 'Version',
              width: '110px',
              render: (i) => <span className="font-mono text-text-primary">{i.version}</span>,
            },
            {
              key: 'size',
              label: 'Size',
              width: '100px',
              render: (i) => <span className="tabular-nums text-text-tertiary">{formatSize(i.fileSize)}</span>,
            },
            {
              key: 'uploaded',
              label: 'Uploaded',
              width: '110px',
              render: (i) => <span className="tabular-nums text-text-tertiary">{i.createdAt.slice(0, 10)}</span>,
            },
            {
              key: 'actions',
              label: '',
              width: '72px',
              render: (i) => (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => void handleDownload(i)}
                    disabled={downloadingId === i.id}
                    aria-label="Download installer"
                    title="Download"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleting(i)}
                    aria-label="Delete installer"
                    title="Delete"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-state-danger/10 hover:text-state-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      )}

      {uploading && <UploadDialog onClose={() => setUploading(false)} />}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete installer?"
        description={
          deleting
            ? `${osMeta(deleting.os).label} ${deleting.version} will be removed from this server. This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
}

// ── Tenant upload tab ────────────────────────────────────────────────────

/**
 * Password-style input with its own always-visible show/hide toggle. The
 * browser's built-in reveal icon only appears while the field is focused or
 * hovered, so it is hidden here in favour of this one.
 */
function SecretInput({
  value,
  onChange,
  className,
  compact,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Toolbar sizing (py-1.5) to match the neighbouring search box and selects. */
  compact?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'className'>) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={cn('relative', className)}>
      <input
        {...rest}
        type={visible ? 'text' : 'password'}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, 'pr-9 font-mono [&::-ms-clear]:hidden [&::-ms-reveal]:hidden', compact && 'py-1.5')}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide API key' : 'Show API key'}
        aria-pressed={visible}
        title={visible ? 'Hide' : 'Show'}
        className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary"
      >
        {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

/** The tenant upload form, unchanged, shown in a dialog like the Installers tab's upload. */
function TenantUploadDialog({
  initialTenantId,
  initialApiKey,
  onClose,
  onUploaded,
}: {
  initialTenantId: string;
  initialApiKey: string;
  onClose: () => void;
  /** Called with the tenant the file was delivered to. */
  onUploaded: (tenantId: string) => void;
}) {
  const [tenantId, setTenantId] = useState(initialTenantId);
  const [apiKey, setApiKey] = useState(initialApiKey);
  const { data } = useTenants(1, 500);
  const tenants = data?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="max-h-full w-full max-w-md space-y-3 overflow-y-auto rounded-lg border border-border-default bg-surface-0 p-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Upload installer for a tenant"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">Upload installer for a tenant</h2>
            <p className="mt-0.5 text-xs text-text-secondary">
              Delivered only to the selected tenant&apos;s client server for the chosen OS and version.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Tenant</label>
          <select className={inputCls} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">Select tenant…</option>
            {tenants.map((t) => (
              <option key={t.tenantId} value={t.tenantId}>
                {t.name} ({t.tenantId})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Tenant console API key</label>
          <SecretInput
            placeholder="INGEST_API_KEY of the tenant console"
            value={apiKey}
            onChange={setApiKey}
          />
          <p className="text-[11px] text-text-tertiary">Used for this upload only; it is not stored.</p>
        </div>

        {/* tenantId="" keeps Upload disabled until a tenant is picked, so a
            tenant upload can never silently become a general one. The file is
            forwarded to that tenant's console, not stored on this server. */}
        <UploadFields
          tenantId={tenantId}
          apiKey={apiKey}
          onUploaded={() => {
            onUploaded(tenantId);
            onClose();
          }}
          footerLeading={
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
          }
        />
      </div>
    </div>
  );
}

function TenantUploadTab() {
  const [tenantId, setTenantId] = useState('');
  const [apiKey, setApiKey] = useState('');
  // Set by "Load"; cleared whenever the tenant or key changes so the table is
  // never shown for a different tenant/key than the toolbar displays.
  const [loaded, setLoaded] = useState<TenantConsoleTarget | null>(null);
  const [search, setSearch] = useState('');
  const [osFilter, setOsFilter] = useState<AgentOs | ''>('');
  const [uploading, setUploading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<TenantInstaller | null>(null);
  const { data: tenantsData } = useTenants(1, 500);
  const tenants = tenantsData?.data ?? [];
  const invalidateList = useInvalidateTenantInstallers();
  const { data, isLoading, isError, error, refetch } = useTenantInstallers(loaded);
  const { mutate: deleteInstaller, isPending: isDeleting, error: deleteError, reset: resetDelete } =
    useDeleteTenantInstaller();

  const rowKey = (i: TenantInstaller) => `${i.os}:${i.version}`;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter(
      (i) =>
        (!osFilter || i.os === osFilter) &&
        (!q || i.fileName.toLowerCase().includes(q) || i.version.toLowerCase().includes(q)),
    );
  }, [data, search, osFilter]);

  const load = () => {
    const next = { tenantId, apiKey: apiKey.trim() };
    if (loaded && loaded.tenantId === next.tenantId && loaded.apiKey === next.apiKey) {
      void refetch();
    } else {
      setLoaded(next);
    }
  };

  const handleDownload = async (i: TenantInstaller) => {
    if (!loaded) return;
    setDownloadingKey(rowKey(i));
    try {
      await downloadTenantInstaller(loaded, i);
    } catch (err) {
      showToast(getErrorMessage(err, 'Download failed'), 'error');
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleting || !loaded) return;
    deleteInstaller(
      { ...loaded, os: deleting.os, version: deleting.version },
      {
        onSuccess: () => {
          showToast('Installer deleted');
          setDeleting(null);
        },
        onError: () => setDeleting(null),
      },
    );
  };

  return (
    <Section>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          className={cn(inputCls, 'w-auto max-w-[14rem] py-1.5')}
          value={tenantId}
          onChange={(e) => {
            setTenantId(e.target.value);
            setLoaded(null);
          }}
          aria-label="Tenant"
        >
          <option value="">Select tenant…</option>
          {tenants.map((t) => (
            <option key={t.tenantId} value={t.tenantId}>
              {t.name} ({t.tenantId})
            </option>
          ))}
        </select>
        <SecretInput
          aria-label="Tenant console API key"
          compact
          className="w-56"
          placeholder="Tenant console API key"
          value={apiKey}
          onChange={(v) => {
            setApiKey(v);
            setLoaded(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && tenantId && apiKey.trim() && load()}
        />
        <Button variant="secondary" size="sm" disabled={!tenantId || !apiKey.trim()} onClick={load}>
          {loaded ? 'Refresh' : 'Load'}
        </Button>
        <div className="relative w-56">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search installers…"
            className={cn(inputCls, 'py-1.5 pl-8')}
          />
        </div>
        <select
          className={cn(inputCls, 'w-auto py-1.5')}
          value={osFilter}
          onChange={(e) => setOsFilter(e.target.value as AgentOs | '')}
        >
          <option value="">All OS</option>
          {OS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="sm"
          className="ml-auto"
          leadingIcon={<Upload className="h-3.5 w-3.5" />}
          onClick={() => setUploading(true)}
        >
          Upload installer
        </Button>
      </div>

      {!loaded ? (
        <EmptyState
          icon={<Upload />}
          title="Select a tenant"
          description="Choose a tenant and enter its console API key, then click Load to see its installers"
          size="sm"
        />
      ) : isError ? (
        <ErrorCard title="Failed to load installers" message={getErrorMessage(error)} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonTable rows={4} />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={<Upload />}
          title="No installers yet"
          description="Upload your first installer for this tenant to get started"
          size="sm"
        />
      ) : (
        <DataTable<TenantInstaller>
          rows={rows}
          empty="No installers match your filters."
          columns={[
            {
              key: 'file',
              label: 'File',
              render: (i) => (
                <div className="flex items-center gap-2">
                  <FileIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                  <span className="truncate font-mono text-text-primary">{i.fileName}</span>
                </div>
              ),
            },
            {
              key: 'os',
              label: 'OS',
              width: '110px',
              render: (i) => (
                <span
                  className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold', osMeta(i.os).badge)}
                >
                  {osMeta(i.os).label}
                </span>
              ),
            },
            {
              key: 'version',
              label: 'Version',
              width: '110px',
              render: (i) => (
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-text-primary">{i.version}</span>
                  {i.isLatest && (
                    <span className="rounded-full bg-state-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-state-success">
                      latest
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: 'size',
              label: 'Size',
              width: '100px',
              render: (i) => <span className="tabular-nums text-text-tertiary">{formatSize(i.fileSize)}</span>,
            },
            {
              key: 'uploaded',
              label: 'Uploaded',
              width: '110px',
              render: (i) => <span className="tabular-nums text-text-tertiary">{i.createdAt.slice(0, 10)}</span>,
            },
            {
              key: 'actions',
              label: '',
              width: '72px',
              render: (i) => (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => void handleDownload(i)}
                    disabled={downloadingKey === rowKey(i)}
                    aria-label="Download installer"
                    title="Download"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      resetDelete();
                      setDeleting(i);
                    }}
                    aria-label="Delete installer"
                    title="Delete"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-state-danger/10 hover:text-state-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      )}

      {deleteError && <p className="mt-2 text-xs text-state-danger">{getErrorMessage(deleteError)}</p>}

      {uploading && (
        <TenantUploadDialog
          initialTenantId={tenantId}
          initialApiKey={apiKey}
          onClose={() => setUploading(false)}
          onUploaded={(uploadedTo) => {
            if (loaded?.tenantId === uploadedTo) void invalidateList(uploadedTo);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete installer?"
        description={
          deleting
            ? `${osMeta(deleting.os).label} ${deleting.version} will be removed from this tenant's console.${
                deleting.isLatest
                  ? ' It is the version agents are offered now; the next-highest stored version (if any) takes its place.'
                  : ''
              } This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('installers');
  const tabs: { id: Tab; label: string }[] = [
    { id: 'installers', label: 'Installers' },
    { id: 'tenant', label: 'Tenant upload' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Settings" description="Manage installer packages distributed to clients." />

      <div className="flex gap-1 border-b border-border-subtle">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors',
              tab === t.id
                ? 'border-accent text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'installers' ? <InstallersTab /> : <TenantUploadTab />}
    </div>
  );
}
